// Müşteri profili › "KPI erişimi" kartı (TODO-KPI-19): firmanın müşteri kullanıcıları (yönetici +
// davetli/aktif) ve set başına "düzenler: … · görüntüler: …" özeti; "Düzenle" → set kutuları +
// kategori daraltma (tavan = Set Yönetimi'ndeki clientAccess, kapalı kategori görünmez);
// "+ Kullanıcı davet et" → aynı editör, davet modunda (ad + e-posta + set erişimi).
// İzin: kpi.manage | clients.edit_all (backend assert'i ile aynı); izinsizde render edilmez.
// Müşteri yöneticisi akışı (portal › Kullanıcılar) korunur — iki yol aynı `setKpiAccess`'e iner.
// Sektör paketleri DR21: set yoksa sektöre eşleşen paket ipucu (`ClientPackageHint`).

import { useEffect, useState } from "react";
import type { ClientAdminSet, ClientEntity, ClientUser } from "@mahir/shared";
import { listClientKpiUsers } from "../../api/client-admin";
import { CLIENT_KPI_ACCESS_CARD_COPY as C } from "../../lib/client-admin-copy";
import { accessSummaryLine, canEditAccess, sortKpiUsers } from "../../lib/client-kpi-access-ui";
import { useAuthStore } from "../../stores/auth-store";
import { ClientPackageHint } from "./ClientPackageHint";
import { KpiAccessEditor } from "./KpiAccessEditor";

interface Props {
  clientId: string;
  /** müşteri kaydındaki sektör (paket ipucu) */
  sector?: string | null | undefined;
}

/** Editör durumu: düzenlemede yalnız kullanıcı kimliği tutulur — yeniden yüklenince güncel satır okunur. */
type EditorTarget = { mode: "invite" } | { mode: "edit"; userId: string };

export function ClientKpiAccessCard({ clientId, sector }: Props): JSX.Element | null {
  const me = useAuthStore((s) => s.user);
  const canManage =
    me?.permissions["kpi.manage"] === true || me?.permissions["clients.edit_all"] === true;

  const [users, setUsers] = useState<ClientUser[] | null>(null);
  const [sets, setSets] = useState<ClientAdminSet[]>([]);
  const [entities, setEntities] = useState<ClientEntity[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [editor, setEditor] = useState<EditorTarget | null>(null);
  const [notice, setNotice] = useState<{ kind: "ok" | "warn"; text: string } | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!canManage) return;
    let aborted = false;
    listClientKpiUsers(clientId)
      .then((res) => {
        if (aborted) return;
        setUsers(res.users);
        setSets(res.sets);
        setEntities(res.entities);
        setError(null);
      })
      .catch(() => {
        if (!aborted) setError(C.loadError);
      });
    return () => {
      aborted = true;
    };
  }, [clientId, canManage, reloadKey]);

  if (!canManage) return null;

  /** 409 STALE sonrası: kullanıcıları sessizce yeniden yükle (editör açık kalır, taslak sıfırlanır). */
  async function refreshUsers(): Promise<void> {
    const res = await listClientKpiUsers(clientId);
    setUsers(res.users);
    setSets(res.sets);
    setEntities(res.entities);
  }

  const editingUser =
    editor?.mode === "edit" ? (users?.find((x) => x.id === editor.userId) ?? null) : null;
  const sorted = users ? sortKpiUsers(users) : [];

  return (
    <div
      data-testid="client-kpi-access-card"
      className="rounded-xl p-5"
      style={{ background: "var(--color-surface)", border: "1px solid var(--color-border)" }}
    >
      <div className="flex items-center justify-between mb-1">
        <h3
          style={{
            fontFamily: "var(--font-display)",
            fontSize: "0.95rem",
            color: "var(--color-text)",
          }}
        >
          {C.title}
        </h3>
        <button
          type="button"
          onClick={() => setEditor({ mode: "invite" })}
          className="text-xs"
          style={{ color: "var(--color-primary)", fontWeight: 600 }}
        >
          + {C.invite}
        </button>
      </div>
      <p className="text-xs mb-3" style={{ color: "var(--color-text-3)" }}>
        {C.hint}
      </p>

      {error ? (
        <div className="text-xs flex items-center gap-2" style={{ color: "var(--color-error)" }}>
          {error}
          <button
            type="button"
            onClick={() => setReloadKey((k) => k + 1)}
            style={{ color: "var(--color-primary)", fontWeight: 600 }}
          >
            {C.retry}
          </button>
        </div>
      ) : users === null ? (
        <div className="text-xs" style={{ color: "var(--color-text-3)" }}>
          …
        </div>
      ) : (
        <>
          {sets.length === 0 ? (
            <div
              className="rounded-md px-3 py-2 text-xs mb-3"
              style={{
                background: "var(--color-warning-bg)",
                color: "var(--color-warning-text)",
                border: "1px solid var(--color-warning-border)",
              }}
            >
              {C.noSets}
            </div>
          ) : null}
          <ClientPackageHint clientId={clientId} sector={sector} setCount={sets.length} />
          {sorted.length === 0 ? (
            <div className="text-xs" style={{ color: "var(--color-text-3)" }}>
              {C.empty}
            </div>
          ) : (
            <ul className="grid gap-2">
              {sorted.map((u) => (
                <li
                  key={u.id}
                  data-testid="kpi-access-row"
                  className="flex items-start justify-between gap-3 text-sm rounded-lg px-3 py-2"
                  style={{ border: "1px solid var(--color-border)" }}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span style={{ color: "var(--color-text)", fontWeight: 500 }}>
                        {u.fullName}
                      </span>
                      {u.isAdmin ? (
                        <span
                          className="px-2 py-0.5 rounded-full"
                          style={{
                            fontSize: "0.65rem",
                            background: "color-mix(in srgb, var(--color-primary) 10%, white)",
                            color: "var(--color-primary)",
                            fontWeight: 600,
                            textTransform: "uppercase",
                          }}
                        >
                          {C.adminTag}
                        </span>
                      ) : null}
                      <span
                        className="text-xs"
                        style={{
                          color:
                            u.status === "active"
                              ? "var(--color-success)"
                              : u.status === "closed"
                                ? "var(--color-text-3)"
                                : "var(--color-warning-text)",
                          fontWeight: 600,
                          fontSize: "0.65rem",
                        }}
                      >
                        {C.statusLabel(u.status)}
                      </span>
                    </div>
                    <div
                      className="text-xs truncate"
                      style={{ color: "var(--color-text-3)", fontFamily: "var(--font-mono)" }}
                    >
                      {u.email}
                    </div>
                    <div className="mt-1 flex flex-col gap-0.5">
                      {u.kpiAccess.length === 0 ? (
                        <span className="text-xs" style={{ color: "var(--color-text-3)" }}>
                          {C.access.none}
                        </span>
                      ) : (
                        u.kpiAccess.map((a) => (
                          <div key={a.setId} className="text-xs">
                            <strong style={{ color: "var(--color-text)" }}>{a.setName}</strong>
                            <span style={{ color: "var(--color-text-2)" }}>
                              {" "}
                              · {accessSummaryLine(a, C.access)}
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setEditor({ mode: "edit", userId: u.id })}
                    disabled={!canEditAccess(u)}
                    title={!canEditAccess(u) ? C.editor.closedReason : undefined}
                    className="text-xs shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ color: "var(--color-primary)", fontWeight: 600 }}
                  >
                    {C.edit}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {notice ? (
        <div
          role="status"
          className="mt-3 rounded-md px-3 py-2 text-xs"
          style={
            notice.kind === "warn"
              ? {
                  background: "var(--color-warning-bg)",
                  color: "var(--color-warning-text)",
                  border: "1px solid var(--color-warning-border)",
                }
              : {
                  background: "color-mix(in srgb, var(--color-success) 12%, white)",
                  color: "var(--color-success)",
                }
          }
        >
          {notice.text}
        </div>
      ) : null}

      {editor && (editor.mode === "invite" || editingUser) ? (
        <KpiAccessEditor
          clientId={clientId}
          sets={sets}
          entities={entities}
          state={
            editor.mode === "invite" ? { mode: "invite" } : { mode: "edit", user: editingUser! }
          }
          onStale={refreshUsers}
          onClose={() => setEditor(null)}
          onSaved={(updated) => {
            setUsers((prev) =>
              prev ? prev.map((x) => (x.id === updated.id ? updated : x)) : [updated],
            );
            setNotice({ kind: "ok", text: C.editor.saved });
            setEditor(null);
          }}
          onInvited={(mailSent, existingActive) => {
            setNotice(
              existingActive
                ? { kind: "ok", text: C.editor.accessGrantedNoMail }
                : mailSent
                  ? { kind: "ok", text: C.editor.invited }
                  : { kind: "warn", text: C.editor.mailWarning },
            );
            setEditor(null);
            setReloadKey((k) => k + 1);
          }}
        />
      ) : null}
    </div>
  );
}
